import { useEffect, useMemo, useState } from "react";
import {
    forwardGraphMessage,
    getConversationMessages,
    getConversations,
    getGraphStatus,
    replyAllGraphMessage,
    replyGraphMessage,
    sendGraphMail,
} from "../../services/conversation_service";
import { ensureSubjectPrefix, mergeConversations } from "../../pages/contacts/utils/contactDetailUtils";

function useContactEmailLog({
    contactId,
    contact,
    initialContact,
    activeTab,
    refreshToken = 0,
    onRefreshRequested,
}) {
    const [emailConversations, setEmailConversations] = useState([]);
    const [emailConversationsLoading, setEmailConversationsLoading] = useState(false);
    const [emailConversationsError, setEmailConversationsError] = useState("");
    const [emailMessages, setEmailMessages] = useState([]);
    const [emailMessagesLoading, setEmailMessagesLoading] = useState(false);
    const [emailMessagesError, setEmailMessagesError] = useState("");
    const [emailPage, setEmailPage] = useState(1);
    const [emailTotalPages, setEmailTotalPages] = useState(0);
    const [emailSearchQuery, setEmailSearchQuery] = useState("");
    const [selectedEmailThreadId, setSelectedEmailThreadId] = useState("");
    const [emailComposeMode, setEmailComposeMode] = useState(false);
    const [emailComposeAction, setEmailComposeAction] = useState({
        type: "new",
        messageId: "",
    });
    const [emailForm, setEmailForm] = useState({
        to: "",
        subject: "",
        body: "",
    });
    const [emailSending, setEmailSending] = useState(false);
    const [emailFeedback, setEmailFeedback] = useState("");
    const [emailGraphConnected, setEmailGraphConnected] = useState(false);

    useEffect(() => {
        if ((activeTab !== "Emails" && activeTab !== "Activity") || !contactId) {
            return;
        }

        let cancelled = false;
        setEmailConversationsLoading(true);
        setEmailConversationsError("");

        const normalizedSearch = String(emailSearchQuery || "").trim();
        const contactEmail = String(contact?.email || initialContact?.email || "").trim();
        const fallbackName = String(
            contact?.name ||
            (contact?.first_name && contact?.last_name
                ? `${contact.first_name} ${contact.last_name}`
                : initialContact?.name || "")
        ).trim();
        const folders = ["inbox", "sent"];

        async function loadConversations() {
            try {
                const primaryPayloads = await Promise.all(
                    folders.map((folder) =>
                        getConversations({
                            folder,
                            page: emailPage,
                            pageSize: 20,
                            query: normalizedSearch,
                            sort: "newest",
                            contactId,
                        })
                    )
                );

                let rows = mergeConversations(primaryPayloads);
                let totalPages = rows.length > 0 ? 1 : 0;

                if (rows.length === 0 && !normalizedSearch) {
                    const fallbackQueries = [contactEmail, fallbackName].filter(Boolean);
                    const fallbackPayloads = [];

                    for (const query of fallbackQueries) {
                        const queryPayloads = await Promise.all(
                            folders.map((folder) =>
                                getConversations({
                                    folder,
                                    page: 1,
                                    pageSize: 50,
                                    query,
                                    sort: "newest",
                                })
                            )
                        );
                        fallbackPayloads.push(...queryPayloads);
                    }

                    rows = mergeConversations(fallbackPayloads);
                    if (rows.length > 0) {
                        totalPages = 1;
                    }
                }

                if (cancelled) {
                    return;
                }

                setEmailConversations(rows);
                setEmailTotalPages(totalPages);

                if (rows.length === 0) {
                    setSelectedEmailThreadId("");
                    return;
                }

                const currentSelection = String(selectedEmailThreadId || "");
                const hasSelection = rows.some((item) => {
                    const key = String(item?.thread_id || item?.id || "");
                    return key && key === currentSelection;
                });

                if (!hasSelection) {
                    const first = rows[0];
                    setSelectedEmailThreadId(String(first?.thread_id || first?.id || ""));
                }
            } catch (err) {
                if (!cancelled) {
                    setEmailConversations([]);
                    setEmailTotalPages(0);
                    setSelectedEmailThreadId("");
                    setEmailConversationsError(err?.message || "Failed to load conversations");
                }
            } finally {
                if (!cancelled) {
                    setEmailConversationsLoading(false);
                }
            }
        }

        loadConversations();

        return () => {
            cancelled = true;
        };
    }, [
        activeTab,
        contactId,
        contact?.email,
        contact?.name,
        contact?.first_name,
        contact?.last_name,
        emailPage,
        emailSearchQuery,
        initialContact?.email,
        initialContact?.name,
        selectedEmailThreadId,
        refreshToken,
    ]);

    useEffect(() => {
        if ((activeTab !== "Emails" && activeTab !== "Activity") || !selectedEmailThreadId || emailComposeMode) {
            setEmailMessages([]);
            setEmailMessagesLoading(false);
            setEmailMessagesError("");
            return;
        }

        let cancelled = false;
        setEmailMessagesLoading(true);
        setEmailMessagesError("");

        const selectedConversation = emailConversations.find(
            (item) => String(item?.thread_id || item?.id || "") === String(selectedEmailThreadId || "")
        );

        const mergedThreadIds = Array.isArray(selectedConversation?.thread_ids)
            ? selectedConversation.thread_ids
            : [selectedEmailThreadId];
        const uniqueThreadIds = Array.from(new Set(mergedThreadIds.map((value) => String(value || "").trim()).filter(Boolean)));

        Promise.all(uniqueThreadIds.map((threadId) => getConversationMessages(threadId)))
            .then((responses) => {
                if (!cancelled) {
                    const allRows = responses.flatMap((rows) => (Array.isArray(rows) ? rows : []));
                    const dedupedRows = [];
                    const seen = new Set();

                    allRows.forEach((row) => {
                        const key = String(row?.graph_message_id || row?.id || "").trim();
                        if (!key || seen.has(key)) {
                            return;
                        }
                        seen.add(key);
                        dedupedRows.push(row);
                    });

                    dedupedRows.sort((a, b) => {
                        const aTime = Date.parse(String(a?.sent_at || "")) || 0;
                        const bTime = Date.parse(String(b?.sent_at || "")) || 0;
                        return bTime - aTime;
                    });

                    setEmailMessages(dedupedRows);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setEmailMessages([]);
                    setEmailMessagesError(err?.message || "Failed to load messages");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setEmailMessagesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [activeTab, emailComposeMode, emailConversations, selectedEmailThreadId]);

    useEffect(() => {
        if (activeTab !== "Emails" && activeTab !== "Activity") {
            return;
        }

        let cancelled = false;
        getGraphStatus()
            .then((payload) => {
                if (!cancelled) {
                    setEmailGraphConnected(Boolean(payload?.connected));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setEmailGraphConnected(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [activeTab]);

    const selectedEmailConversation = useMemo(() => {
        return emailConversations.find((item) => String(item?.thread_id || item?.id || "") === String(selectedEmailThreadId || "")) || null;
    }, [emailConversations, selectedEmailThreadId]);

    function handleEmailSearchChange(value) {
        setEmailSearchQuery(value);
        setEmailPage(1);
    }

    function handleEmailStartCompose() {
        if (!emailGraphConnected) {
            setEmailFeedback("Connect Microsoft 365 before sending email.");
            return;
        }

        setEmailComposeAction({ type: "new", messageId: "" });
        setEmailFeedback("");
        setEmailComposeMode(true);
        setEmailForm((prev) => ({
            ...prev,
            to: prev.to || contact?.email || initialContact?.email || "",
        }));
    }

    function handleEmailCancelCompose() {
        setEmailComposeMode(false);
        setEmailComposeAction({ type: "new", messageId: "" });
        setEmailFeedback("");
    }

    function handleEmailReplyFromMessage(message) {
        if (!emailGraphConnected) {
            setEmailFeedback("Connect Microsoft 365 before sending email.");
            return;
        }

        const messageId = String(message?.graph_message_id || "").trim();
        if (!messageId) {
            setEmailFeedback("Reply is unavailable for this message.");
            return;
        }

        const subject = ensureSubjectPrefix(message?.subject || selectedEmailConversation?.subject, "Re: ");

        setEmailFeedback("");
        setEmailComposeAction({ type: "reply", messageId });
        setEmailForm({
            to: "",
            subject,
            body: "",
        });
        setEmailComposeMode(true);
    }

    function handleEmailReplyAllFromMessage(message) {
        if (!emailGraphConnected) {
            setEmailFeedback("Connect Microsoft 365 before sending email.");
            return;
        }

        const messageId = String(message?.graph_message_id || "").trim();
        if (!messageId) {
            setEmailFeedback("Reply All is unavailable for this message.");
            return;
        }

        const subject = ensureSubjectPrefix(message?.subject || selectedEmailConversation?.subject, "Re: ");

        setEmailFeedback("");
        setEmailComposeAction({ type: "replyAll", messageId });
        setEmailForm({
            to: "",
            subject,
            body: "",
        });
        setEmailComposeMode(true);
    }

    function handleEmailForwardFromMessage(message) {
        if (!emailGraphConnected) {
            setEmailFeedback("Connect Microsoft 365 before sending email.");
            return;
        }

        const messageId = String(message?.graph_message_id || "").trim();
        if (!messageId) {
            setEmailFeedback("Forward is unavailable for this message.");
            return;
        }

        setEmailFeedback("");
        setEmailComposeAction({ type: "forward", messageId });
        setEmailForm({
            to: "",
            subject: ensureSubjectPrefix(message?.subject, "Fwd: "),
            body: "",
        });
        setEmailComposeMode(true);
    }

    async function handleEmailSend() {
        const composeType = emailComposeAction?.type || "new";
        const composeMessageId = String(emailComposeAction?.messageId || "").trim();
        const toValue = String(emailForm.to || "").trim();
        const subjectValue = String(emailForm.subject || "").trim();
        const bodyValue = String(emailForm.body || "");

        if ((composeType === "new" || composeType === "forward") && !toValue) {
            setEmailFeedback("Recipient is required.");
            return;
        }
        if ((composeType === "new" || composeType === "forward") && !subjectValue) {
            setEmailFeedback("Subject is required.");
            return;
        }
        if ((composeType === "reply" || composeType === "replyAll" || composeType === "forward") && !composeMessageId) {
            setEmailFeedback("Selected message is unavailable for this action.");
            return;
        }

        setEmailSending(true);
        setEmailFeedback("");

        try {
            if (composeType === "reply") {
                await replyGraphMessage({ messageId: composeMessageId, body: bodyValue });
            } else if (composeType === "replyAll") {
                await replyAllGraphMessage({ messageId: composeMessageId, body: bodyValue });
            } else if (composeType === "forward") {
                await forwardGraphMessage({ messageId: composeMessageId, to: toValue, body: bodyValue });
            } else {
                await sendGraphMail({
                    to: toValue,
                    subject: subjectValue,
                    body: bodyValue,
                });
            }

            const nowIso = new Date().toISOString();
            const optimisticMessage = {
                id: `temp-${Date.now()}`,
                direction: "outbound",
                from_name: "You",
                to_email: toValue || selectedEmailConversation?.contact_email || contact?.email || "",
                subject: subjectValue || selectedEmailConversation?.subject || "(no subject)",
                body_text: bodyValue,
                sent_at: nowIso,
            };

            setEmailMessages((prev) => [optimisticMessage, ...(Array.isArray(prev) ? prev : [])]);
            setEmailConversations((prev) => {
                if (!Array.isArray(prev)) {
                    return prev;
                }
                return prev
                    .map((conversation) => {
                        const key = String(conversation?.thread_id || conversation?.id || "");
                        if (key !== String(selectedEmailThreadId || "")) {
                            return conversation;
                        }
                        return {
                            ...conversation,
                            subject: subjectValue || conversation?.subject || "(no subject)",
                            updated_at: nowIso,
                            message_count: Number(conversation?.message_count || 0) + 1,
                        };
                    })
                    .sort((a, b) => {
                        const aTime = Date.parse(String(a?.updated_at || "")) || 0;
                        const bTime = Date.parse(String(b?.updated_at || "")) || 0;
                        return bTime - aTime;
                    });
            });

            setEmailFeedback("Email sent.");
            setEmailComposeMode(false);
            setEmailComposeAction({ type: "new", messageId: "" });
            setEmailForm({
                to: contact?.email || initialContact?.email || "",
                subject: "",
                body: "",
            });
            onRefreshRequested?.();
        } catch (err) {
            setEmailFeedback(err?.message || "Failed to send email.");
        } finally {
            setEmailSending(false);
        }
    }

    return {
        emailConversations,
        emailConversationsLoading,
        emailConversationsError,
        emailMessages,
        emailMessagesLoading,
        emailMessagesError,
        emailPage,
        emailTotalPages,
        emailSearchQuery,
        selectedEmailThreadId,
        selectedEmailConversation,
        emailComposeMode,
        emailComposeAction,
        emailForm,
        emailSending,
        emailFeedback,
        emailGraphConnected,
        onEmailSearchChange: handleEmailSearchChange,
        onEmailPrevPage: () => setEmailPage((value) => Math.max(1, value - 1)),
        onEmailNextPage: () => setEmailPage((value) => Math.min(Math.max(emailTotalPages, 1), value + 1)),
        onEmailSelectConversation: (threadId) => {
            setEmailComposeMode(false);
            setEmailComposeAction({ type: "new", messageId: "" });
            setSelectedEmailThreadId(String(threadId || ""));
        },
        onEmailComposeChange: (field, value) => {
            setEmailForm((prev) => ({ ...prev, [field]: value }));
        },
        onEmailStartCompose: handleEmailStartCompose,
        onEmailCancelCompose: handleEmailCancelCompose,
        onEmailSend: handleEmailSend,
        onEmailReply: handleEmailReplyFromMessage,
        onEmailReplyAll: handleEmailReplyAllFromMessage,
        onEmailForward: handleEmailForwardFromMessage,
    };
}

export default useContactEmailLog;